// src/components/forms/RegisterForm.js 
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, MapPin, Ambulance as AmbulanceIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';

const RegisterForm = () => {
  const navigate = useNavigate();
  const { register, isLoading } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '', 
    email: '', 
    phone: '', 
    password: '', 
    confirmPassword: '', 
    role: '',
    location: { 
      address: '', 
      city: '', 
      country: 'Kenya',
      coordinates: null
    },
    // Doctor fields
    firstName: '', 
    lastName: '', 
    specialization: '', 
    qualifications: '', 
    experience: '',
    licenseNumber: '', 
    consultationFee: { online: '', physical: '' },
    hospitalId: '', 
    hospitalDetails: { name: '', address: '', phone: '', email: '' },
    // Ambulance fields 
    ambulanceName: '',
    description: '',  
    isIndependent: true,
    serviceAreas: [],
    baseFee: '',
    perKmRate: '',
    operatingHours: {
      is24Hours: true,
    },
    services: [],
    equipment: [],
    capabilities: {
      basicLifeSupport: true,
      advancedLifeSupport: false,
      criticalCare: false
    }
  });
  
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNewHospitalForm, setShowNewHospitalForm] = useState(false);
  const [hospitals, setHospitals] = useState([]);
  const [loadingHospitals, setLoadingHospitals] = useState(false);
  const [hospitalSearch, setHospitalSearch] = useState('');

  const roles = [
    { value: '', label: 'Select your role' },
    { value: 'patient', label: 'Patient' },
    { value: 'doctor', label: 'Doctor' },
    { value: 'hospital', label: 'Hospital' },
    { value: 'pharmacy', label: 'Pharmacy' },
    { value: 'ambulance', label: 'Ambulance Service' }
  ];

  const specializations = [
    'General Medicine', 'Cardiology', 'Dermatology', 'Emergency Medicine',
    'Family Medicine', 'Internal Medicine', 'Neurology', 'Obstetrics and Gynecology',
    'Oncology', 'Ophthalmology', 'Orthopedics', 'Pediatrics', 'Psychiatry', 
    'Radiology', 'Surgery', 'Urology'
  ];

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const fetchHospitals = async (searchTerm = '') => {
    setLoadingHospitals(true);
    try {
      const response = await apiService.auth.getHospitalsForRegistration(searchTerm, 50);
      
      if (response && response.success) {
        const hospitalsList = response.data?.hospitals || [];
        setHospitals(hospitalsList);
      } else {
        setHospitals([]);
      }
    } catch (error) {
      setHospitals([]);
    } finally {
      setLoadingHospitals(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((searchTerm) => {
      fetchHospitals(searchTerm);
    }, 500),
    []
  );

  const handleHospitalSearch = (searchTerm) => {
    setHospitalSearch(searchTerm);
    debouncedSearch(searchTerm);
  };

  useEffect(() => {
    if (formData.role === 'doctor' || (formData.role === 'ambulance' && !formData.isIndependent)) {
      fetchHospitals();
    } else {
      setHospitals([]);
      setHospitalSearch('');
    }
  }, [formData.role, formData.isIndependent]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    
    if (name.includes('.')) {
      const keys = name.split('.');
      if (keys.length === 2) {
        setFormData(prev => ({
          ...prev,
          [keys[0]]: { ...prev[keys[0]], [keys[1]]: fieldValue }
        }));
      } else if (keys.length === 3) {
        setFormData(prev => ({
          ...prev,
          [keys[0]]: {
            ...prev[keys[0]],
            [keys[1]]: { ...prev[keys[0]][keys[1]], [keys[2]]: fieldValue }
          }
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: fieldValue }));
    }
    
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (submitError) setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      // Base registration data
      let registrationData = {
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        password: formData.password,
        role: formData.role,
        location: {
          type: 'Point',
          coordinates: formData.location.coordinates || [36.8219, -1.2921],
          address: formData.location.address?.trim() || '',
          city: formData.location.city?.trim() || '',
          country: formData.location.country || 'Kenya'
        }
      };
      
      switch (formData.role) {
        case 'ambulance':
          // Validate ambulance name
          if (!formData.ambulanceName || formData.ambulanceName.trim().length < 2) {
            setSubmitError('Ambulance service name is required');
            setIsSubmitting(false);
            return;
          }
          
          registrationData = {
            ...registrationData,
            ambulanceName: formData.ambulanceName.trim(),
            name: formData.ambulanceName.trim(),
            description: formData.description?.trim() || `${formData.ambulanceName.trim()} - Professional Ambulance Service`,
            isIndependent: Boolean(formData.isIndependent),
            contact: {
              phone: formData.phone.trim(),
              email: formData.email.trim()
            },
            serviceAreas: Array.isArray(formData.serviceAreas) ? formData.serviceAreas : [],
            operatingHours: formData.operatingHours || { is24Hours: true },
            services: Array.isArray(formData.services) ? formData.services : [],
            equipment: Array.isArray(formData.equipment) ? formData.equipment : [],
            capabilities: formData.capabilities || {
              basicLifeSupport: true,
              advancedLifeSupport: false,
              criticalCare: false
            }
          };
          
          // Add pricing if provided
          if (formData.baseFee || formData.perKmRate) {
            registrationData.pricing = {
              baseFee: formData.baseFee ? parseFloat(formData.baseFee) : 2000,
              perKmRate: formData.perKmRate ? parseFloat(formData.perKmRate) : 50,
              currency: 'KES'
            };
          } else {
            registrationData.baseFee = 2000;
            registrationData.perKmRate = 50;
          }
          
          // Handle hospital association for non-independent ambulances
          if (!formData.isIndependent) {
            if (formData.hospitalId) {
              registrationData.hospitalId = formData.hospitalId;
            } else if (showNewHospitalForm && formData.hospitalDetails.name) {
              registrationData.hospitalDetails = {
                name: formData.hospitalDetails.name.trim(),
                address: formData.hospitalDetails.address.trim(),
                phone: formData.hospitalDetails.phone?.trim() || formData.phone.trim(),
                email: formData.hospitalDetails.email?.trim() || formData.email.trim()
              };
            }
          }
          break;
          
        case 'doctor':
          // Validate doctor fields
          if (!formData.firstName || !formData.lastName) {
            setSubmitError('First name and last name are required');
            setIsSubmitting(false);
            return;
          }
          
          if (!formData.specialization) {
            setSubmitError('Specialization is required');
            setIsSubmitting(false);
            return;
          }
          
          if (!formData.licenseNumber) {
            setSubmitError('License number is required');
            setIsSubmitting(false);
            return;
          }
          
          registrationData = {
            ...registrationData,
            name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            specialization: formData.specialization.trim(),
            qualifications: formData.qualifications?.trim() || '',
            experience: formData.experience ? parseInt(formData.experience) : 0,
            licenseNumber: formData.licenseNumber.trim()
          };
          
          // Add consultation fees if provided
          if (formData.consultationFee.online || formData.consultationFee.physical) {
            registrationData.consultationFee = {
              online: formData.consultationFee.online ? parseFloat(formData.consultationFee.online) : 500,
              physical: formData.consultationFee.physical ? parseFloat(formData.consultationFee.physical) : 1000
            };
          }
          
          // Handle hospital association
          if (formData.hospitalId) {
            registrationData.hospitalId = formData.hospitalId;
          } else if (showNewHospitalForm && formData.hospitalDetails.name) {
            registrationData.hospitalDetails = {
              name: formData.hospitalDetails.name.trim(),
              address: formData.hospitalDetails.address.trim(),
              phone: formData.hospitalDetails.phone?.trim() || formData.phone.trim(),
              email: formData.hospitalDetails.email?.trim() || formData.email.trim()
            };
          }
          break;
          
        default:
          // For patient, hospital, pharmacy, driver roles
          if (!formData.name || formData.name.trim().length < 2) {
            setSubmitError('Name is required');
            setIsSubmitting(false);
            return;
          }
          registrationData = {
            ...registrationData,
            name: formData.name.trim()
          };
          break;
      }
           
      
      const result = await register(registrationData);
      
      if (result.success) {
        navigate('/auth/verify', { 
          state: { 
            message: 'Please check your email/SMS for verification code',
            email: formData.email,
            phone: formData.phone
          } 
        });
      } else {
        setSubmitError(result.message || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle specific error cases
      if (error.message && error.message.includes('E11000 duplicate key error')) {
        if (error.message.includes('email')) {
          setSubmitError('This email is already registered. Please use a different email or login.');
        } else if (error.message.includes('phone')) {
          setSubmitError('This phone number is already registered. Please use a different number or login.');
        } else {
          setSubmitError('An account with these details already exists. Please check your information.');
        }
      } else if (error.message && error.message.includes('validation')) {
        setSubmitError('Please check all required fields and try again.');
      } else {
        setSubmitError(error.message || 'Registration failed. Please check your details and try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Name validation based on role
    if (formData.role === 'ambulance') {
      if (!formData.ambulanceName || formData.ambulanceName.trim().length < 2) {
        newErrors.ambulanceName = 'Ambulance service name is required (min 2 characters)';
      }
    } else if (formData.role !== 'doctor') {
      if (!formData.name || formData.name.trim().length < 2) {
        newErrors.name = 'Full name is required (min 2 characters)';
      }
    }
    
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Valid email is required';
    }
    
    if (!formData.phone || !/^(\+254|0)[0-9]{9}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Valid Kenyan phone number required (+254 or 0)';
    }
    
    if (!formData.password || formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.role) {
      newErrors.role = 'Please select a role';
    }
    
    // Location validation
    if (!formData.location.address) {
      newErrors['location.address'] = 'Address is required';
    }
    
    if (!formData.location.city) {
      newErrors['location.city'] = 'City is required';
    }
    
    // Doctor validation
    if (formData.role === 'doctor') {
      if (!formData.firstName || formData.firstName.trim().length < 2) {
        newErrors.firstName = 'First name is required';
      }
      if (!formData.lastName || formData.lastName.trim().length < 2) {
        newErrors.lastName = 'Last name is required';
      }
      if (!formData.specialization) {
        newErrors.specialization = 'Specialization is required';
      }
      if (!formData.licenseNumber) {
        newErrors.licenseNumber = 'License number is required';
      }
      
      if (!formData.hospitalId && !showNewHospitalForm) {
        newErrors.hospitalId = 'Please select a hospital or create a new one';
      }
      
      if (showNewHospitalForm) {
        if (!formData.hospitalDetails.name) {
          newErrors['hospitalDetails.name'] = 'Hospital name is required';
        }
        if (!formData.hospitalDetails.address) {
          newErrors['hospitalDetails.address'] = 'Hospital address is required';
        }
      }
    }
    
    // Ambulance validation
    if (formData.role === 'ambulance') {
      if (!formData.ambulanceName || formData.ambulanceName.trim().length < 2) {
        newErrors.ambulanceName = 'Ambulance service name is required';
      }
      
      // Hospital requirement for non-independent ambulances
      if (!formData.isIndependent) {
        if (!formData.hospitalId && !showNewHospitalForm) {
          newErrors.hospitalId = 'Please select a hospital or create a new one';
        }
        
        if (showNewHospitalForm) {
          if (!formData.hospitalDetails.name) {
            newErrors['hospitalDetails.name'] = 'Hospital name is required';
          }
          if (!formData.hospitalDetails.address) {
            newErrors['hospitalDetails.address'] = 'Hospital address is required';
          }
        }
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isDoctorRole = formData.role === 'doctor';
  const isAmbulanceRole = formData.role === 'ambulance';

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-indigo-100 flex items-center justify-center px-4 py-6 sm:py-8 lg:py-12">
      <div className="w-full max-w-md lg:max-w-3xl xl:max-w-4xl mx-auto p-4 sm:p-6 md:p-8 lg:p-10 bg-white rounded-lg shadow-xl my-4 sm:my-8">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-2">Join MediGo</h2>
        <p className="text-center text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">Create your account</p>
        
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {submitError && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm sm:text-base">
              {submitError}
            </div>
          )}
          
          <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2">
            <h3 className="col-span-1 md:col-span-2 text-base sm:text-lg font-semibold text-gray-900">Basic Information</h3>
            
            {/* Name field - hidden for doctor and ambulance */}
            <div className={isDoctorRole || isAmbulanceRole ? 'hidden' : 'col-span-1 md:col-span-2'}>
              <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Full Name *</label>
              <input 
                type="text" 
                name="name" 
                value={formData.name} 
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                required={!isDoctorRole && !isAmbulanceRole}
              />
              {errors.name && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors.name}</p>}
            </div>

            {/* Doctor name fields */}
            {isDoctorRole && (
              <>
                <div className="col-span-1">
                  <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">First Name *</label>
                  <input 
                    type="text" 
                    name="firstName" 
                    value={formData.firstName} 
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                    required 
                  />
                  {errors.firstName && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors.firstName}</p>}
                </div>
                <div className="col-span-1">
                  <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Last Name *</label>
                  <input 
                    type="text" 
                    name="lastName" 
                    value={formData.lastName} 
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                    required 
                  />
                  {errors.lastName && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors.lastName}</p>}
                </div>
              </>
            )}

            {/* Ambulance name field */}
            {isAmbulanceRole && (
              <div className="col-span-1 md:col-span-2">
                <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700 flex items-center gap-2">
                  <AmbulanceIcon className="w-4 h-4" />
                  Ambulance Service Name *
                </label>
                <input 
                  type="text" 
                  name="ambulanceName" 
                  value={formData.ambulanceName} 
                  onChange={handleChange}
                  placeholder="e.g., Nairobi Emergency Ambulance Services"
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                  required 
                />
                {errors.ambulanceName && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors.ambulanceName}</p>}
              </div>
            )}
            
            <div className="col-span-1">
              <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Email *</label>
              <input 
                type="email" 
                name="email" 
                value={formData.email} 
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                required 
              />
              {errors.email && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors.email}</p>}
            </div>
            
            <div className="col-span-1">
              <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Phone *</label>
              <input 
                type="tel" 
                name="phone" 
                value={formData.phone} 
                onChange={handleChange} 
                placeholder="+254 or 0"
                className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                required 
              />
              {errors.phone && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors.phone}</p>}
            </div>
            
            <div className="relative col-span-1">
              <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Password *</label>
              <input 
                type={showPassword ? 'text' : 'password'} 
                name="password" 
                value={formData.password} 
                onChange={handleChange}
                className="w-full px-3 py-2 pr-10 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                required 
              />
              <button 
                type="button" 
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-8 sm:top-9 text-gray-500 hover:text-gray-700">
                {showPassword ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5" />}
              </button>
              {errors.password && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors.password}</p>}
            </div>
            
            <div className="relative col-span-1">
              <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Confirm Password *</label>
              <input 
                type={showConfirmPassword ? 'text' : 'password'} 
                name="confirmPassword" 
                value={formData.confirmPassword} 
                onChange={handleChange}
                className="w-full px-3 py-2 pr-10 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                required 
              />
              <button 
                type="button" 
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-8 sm:top-9 text-gray-500 hover:text-gray-700">
                {showConfirmPassword ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5" />}
              </button>
              {errors.confirmPassword && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors.confirmPassword}</p>}
            </div>
            
            <div className="col-span-1 md:col-span-2">
              <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Role *</label>
              <select 
                name="role" 
                value={formData.role} 
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                required>
                {roles.map(role => <option key={role.value} value={role.value}>{role.label}</option>)}
              </select>
              {errors.role && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors.role}</p>}
            </div>
            
            <h3 className="col-span-1 md:col-span-2 text-base sm:text-lg font-semibold text-gray-900 mt-2 sm:mt-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Location
            </h3>
            
            <div className="col-span-1 md:col-span-2">
              <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">
                Street Address / Building *
              </label>
              <input 
                type="text" 
                name="location.address" 
                value={formData.location.address} 
                onChange={handleChange}
                placeholder="e.g., Kenyatta Avenue, City Hall Building"
                className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                required 
              />
              {errors['location.address'] && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors['location.address']}</p>}
            </div>
            
            <div className="col-span-1">
              <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">City *</label>
              <input 
                type="text" 
                name="location.city" 
                value={formData.location.city} 
                onChange={handleChange}
                placeholder="e.g., Nairobi"
                className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                required 
              />
              {errors['location.city'] && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors['location.city']}</p>}
            </div>
            
            <div className="col-span-1">
              <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Country</label>
              <input 
                type="text" 
                name="location.country" 
                value={formData.location.country} 
                disabled
                className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed" 
              />
            </div>
            
            {/* Doctor Professional Details */}
            {isDoctorRole && (
              <>
                <h3 className="col-span-1 md:col-span-2 text-base sm:text-lg font-semibold text-gray-900 mt-2 sm:mt-4">Professional Details</h3>
                
                <div className="col-span-1">
                  <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Specialization *</label>
                  <select 
                    name="specialization" 
                    value={formData.specialization} 
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                    required>
                    <option value="">Select...</option>
                    {specializations.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {errors.specialization && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors.specialization}</p>}
                </div>
                
                <div className="col-span-1">
                  <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">License Number *</label>
                  <input 
                    type="text" 
                    name="licenseNumber" 
                    value={formData.licenseNumber} 
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                    required 
                  />
                  {errors.licenseNumber && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors.licenseNumber}</p>}
                </div>
                
                <div className="col-span-1">
                  <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Experience (years)</label>
                  <input 
                    type="number" 
                    name="experience" 
                    value={formData.experience} 
                    onChange={handleChange} 
                    min="0"
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                  />
                </div>
                
                <div className="col-span-1 md:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Qualifications (comma-separated)</label>
                  <input 
                    type="text" 
                    name="qualifications" 
                    value={formData.qualifications} 
                    onChange={handleChange}
                    placeholder="MBBS, MD, etc." 
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                  />
                </div>
                
                <div className="col-span-1">
                  <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Online Fee (KES) *</label>
                  <input 
                    type="number" 
                    name="consultationFee.online" 
                    value={formData.consultationFee.online} 
                    onChange={handleChange}
                    min="0" 
                    step="50" 
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                    required 
                  />
                  {errors['consultationFee.online'] && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors['consultationFee.online']}</p>}
                </div>
                
                <div className="col-span-1">
                  <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Physical Fee (KES) *</label>
                  <input 
                    type="number" 
                    name="consultationFee.physical" 
                    value={formData.consultationFee.physical} 
                    onChange={handleChange}
                    min="0" 
                    step="50" 
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                    required 
                  />
                  {errors['consultationFee.physical'] && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors['consultationFee.physical']}</p>}
                </div>
                
                <h3 className="col-span-1 md:col-span-2 text-base sm:text-lg font-semibold text-gray-900 mt-2 sm:mt-4">Hospital Information</h3>
                
                <div className="col-span-1 md:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">
                    Search Hospital
                  </label>
                  <input
                    type="text"
                    value={hospitalSearch}
                    onChange={(e) => handleHospitalSearch(e.target.value)}
                    placeholder="Type to search hospitals..."
                    className="w-full px-3 py-2 mb-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                  
                  <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Select Hospital *</label>
                  {loadingHospitals ? (
                    <p className="text-xs sm:text-sm text-gray-500 py-2">Loading hospitals...</p>
                  ) : (
                    <select 
                      name="hospitalId" 
                      value={formData.hospitalId || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === 'new') {
                          setShowNewHospitalForm(true);
                          setFormData(p => ({ 
                            ...p, 
                            hospitalId: '',
                            hospitalDetails: { name: '', address: '', phone: '', email: '' }
                          }));
                          setErrors(prev => ({ ...prev, hospitalId: '' }));
                        } else if (value === '') {
                          setShowNewHospitalForm(false);
                          setFormData(p => ({ ...p, hospitalId: '' }));
                        } else {
                          setShowNewHospitalForm(false);
                          setFormData(p => ({ 
                            ...p, 
                            hospitalId: value,
                            hospitalDetails: { name: '', address: '', phone: '', email: '' }
                          }));
                          setErrors(prev => ({ ...prev, hospitalId: '', 'hospitalDetails.name': '', 'hospitalDetails.address': '' }));
                        }
                      }}
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select a hospital</option>
                      {hospitals.length === 0 && !loadingHospitals ? (
                        <option value="" disabled>No hospitals found</option>
                      ) : (
                        hospitals.map(hospital => (
                          <option key={hospital._id} value={hospital._id}>
                            {hospital.name} - {hospital.location?.address || 'N/A'}
                            {hospital.rating?.average > 0 && ` (⭐ ${hospital.rating.average.toFixed(1)})`}
                          </option>
                        ))
                      )}
                      <option value="new" className="font-semibold">➕ Add New Hospital</option>
                    </select>
                  )}
                  {errors.hospitalId && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors.hospitalId}</p>}
                </div>
                
                {showNewHospitalForm && (
                  <>
                    <div className="col-span-1 md:col-span-2 bg-primary-50 border border-primary-200 rounded-lg p-3 mb-2">
                      <p className="text-xs sm:text-sm text-primary-700">Hospital will be verified before approval. You can practice while pending.</p>
                    </div>
                    
                    <div className="col-span-1">
                      <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Hospital Name *</label>
                      <input 
                        type="text" 
                        name="hospitalDetails.name" 
                        value={formData.hospitalDetails.name} 
                        onChange={handleChange}
                        className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                      />
                      {errors['hospitalDetails.name'] && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors['hospitalDetails.name']}</p>}
                    </div>
                    
                    <div className="col-span-1">
                      <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Hospital Address *</label>
                      <input 
                        type="text" 
                        name="hospitalDetails.address" 
                        value={formData.hospitalDetails.address} 
                        onChange={handleChange}
                        placeholder="Will be geocoded automatically"
                        className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                      />
                      {errors['hospitalDetails.address'] && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors['hospitalDetails.address']}</p>}
                    </div>
                    
                    <div className="col-span-1">
                      <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Hospital Phone</label>
                      <input 
                        type="tel" 
                        name="hospitalDetails.phone" 
                        value={formData.hospitalDetails.phone} 
                        onChange={handleChange}
                        className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                      />
                    </div>
                    
                    <div className="col-span-1">
                      <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Hospital Email</label>
                      <input 
                        type="email" 
                        name="hospitalDetails.email" 
                        value={formData.hospitalDetails.email} 
                        onChange={handleChange}
                        className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                      />
                    </div>
                  </>
                )}
              </>
            )}

            {/* Ambulance Service Details */}
            {isAmbulanceRole && (
              <>
                <h3 className="col-span-1 md:col-span-2 text-base sm:text-lg font-semibold text-gray-900 mt-2 sm:mt-4 flex items-center gap-2">
                  <AmbulanceIcon className="w-5 h-5" />
                  Service Details
                </h3>

                <div className="col-span-1 md:col-span-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="isIndependent"
                      checked={formData.isIndependent}
                      onChange={handleChange}
                      className="w-4 h-4 text-primary-600 rounded"
                    />
                    <span className="text-xs sm:text-sm text-gray-700">
                      Independent Ambulance Service (not affiliated with a hospital)
                    </span>
                  </label>
                </div>

                <div className="col-span-1">
                  <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Base Fee (KES)</label>
                  <input 
                    type="number" 
                    name="baseFee" 
                    value={formData.baseFee} 
                    onChange={handleChange}
                    min="0" 
                    step="100" 
                    placeholder="e.g., 2000"
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                  />
                </div>

                <div className="col-span-1">
                  <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Per KM Rate (KES)</label>
                  <input 
                    type="number" 
                    name="perKmRate" 
                    value={formData.perKmRate} 
                    onChange={handleChange}
                    min="0" 
                    step="10" 
                    placeholder="e.g., 50"
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                  />
                </div>

                {/* Hospital Selection for Non-Independent Ambulances */}
                {!formData.isIndependent && (
                  <>
                    <h3 className="col-span-1 md:col-span-2 text-base sm:text-lg font-semibold text-gray-900 mt-2 sm:mt-4">Hospital Association</h3>
                    
                    <div className="col-span-1 md:col-span-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-xs sm:text-sm text-amber-700">
                        <strong>Note:</strong> Your ambulance service will be affiliated with the selected hospital.
                      </p>
                    </div>

                    <div className="col-span-1 md:col-span-2">
                      <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">
                        Search Hospital
                      </label>
                      <input
                        type="text"
                        value={hospitalSearch}
                        onChange={(e) => handleHospitalSearch(e.target.value)}
                        placeholder="Type to search hospitals..."
                        className="w-full px-3 py-2 mb-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                      
                      <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Select Hospital *</label>
                      {loadingHospitals ? (
                        <p className="text-xs sm:text-sm text-gray-500 py-2">Loading hospitals...</p>
                      ) : (
                        <select 
                          name="hospitalId" 
                          value={formData.hospitalId || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === 'new') {
                              setShowNewHospitalForm(true);
                              setFormData(p => ({ 
                                ...p, 
                                hospitalId: '',
                                hospitalDetails: { name: '', address: '', phone: '', email: '' }
                              }));
                              setErrors(prev => ({ ...prev, hospitalId: '' }));
                            } else if (value === '') {
                              setShowNewHospitalForm(false);
                              setFormData(p => ({ ...p, hospitalId: '' }));
                            } else {
                              setShowNewHospitalForm(false);
                              setFormData(p => ({ 
                                ...p, 
                                hospitalId: value,
                                hospitalDetails: { name: '', address: '', phone: '', email: '' }
                              }));
                              setErrors(prev => ({ ...prev, hospitalId: '', 'hospitalDetails.name': '', 'hospitalDetails.address': '' }));
                            }
                          }}
                          className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="">Select a hospital</option>
                          {hospitals.length === 0 && !loadingHospitals ? (
                            <option value="" disabled>No hospitals found</option>
                          ) : (
                            hospitals.map(hospital => (
                              <option key={hospital._id} value={hospital._id}>
                                {hospital.name} - {hospital.location?.address || 'N/A'}
                                {hospital.rating?.average > 0 && ` (⭐ ${hospital.rating.average.toFixed(1)})`}
                              </option>
                            ))
                          )}
                          <option value="new" className="font-semibold">➕ Add New Hospital</option>
                        </select>
                      )}
                      {errors.hospitalId && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors.hospitalId}</p>}
                    </div>
                    
                    {showNewHospitalForm && (
                      <>
                        <div className="col-span-1 md:col-span-2 bg-primary-50 border border-primary-200 rounded-lg p-3 mb-2">
                          <p className="text-xs sm:text-sm text-primary-700">Hospital will be created and linked to your ambulance service. Pending verification.</p>
                        </div>
                        
                        <div className="col-span-1">
                          <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Hospital Name *</label>
                          <input 
                            type="text" 
                            name="hospitalDetails.name" 
                            value={formData.hospitalDetails.name} 
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                          />
                          {errors['hospitalDetails.name'] && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors['hospitalDetails.name']}</p>}
                        </div>
                        
                        <div className="col-span-1">
                          <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Hospital Address *</label>
                          <input 
                            type="text" 
                            name="hospitalDetails.address" 
                            value={formData.hospitalDetails.address} 
                            onChange={handleChange}
                            placeholder="Will be geocoded automatically"
                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                          />
                          {errors['hospitalDetails.address'] && <p className="text-red-600 text-xs sm:text-sm mt-1">{errors['hospitalDetails.address']}</p>}
                        </div>
                        
                        <div className="col-span-1">
                          <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Hospital Phone</label>
                          <input 
                            type="tel" 
                            name="hospitalDetails.phone" 
                            value={formData.hospitalDetails.phone} 
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                          />
                        </div>
                        
                        <div className="col-span-1">
                          <label className="block text-xs sm:text-sm font-medium mb-1 text-gray-700">Hospital Email</label>
                          <input 
                            type="email" 
                            name="hospitalDetails.email" 
                            value={formData.hospitalDetails.email} 
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                          />
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <div className="flex items-start mt-4 sm:mt-6">
            <input 
              id="terms" 
              type="checkbox" 
              className="h-4 w-4 text-primary-600 rounded mt-0.5" 
              required 
            />
            <label htmlFor="terms" className="ml-2 text-xs sm:text-sm text-gray-700">
              I agree to the Terms of Service and Privacy Policy
            </label>
          </div>

          <button 
            type="submit" 
            disabled={isSubmitting || isLoading}
            className="w-full bg-primary-600 text-white py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {isSubmitting || isLoading ? 'Creating Account...' : 'Create Account'}
          </button>

          <p className="text-center text-xs sm:text-sm text-gray-600 mt-4">
            Already have an account? <Link to="/auth/login" className="text-primary-600 hover:underline font-medium">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default RegisterForm;